// @ts-check
// Formatação de valores dos widgets (número, moeda em reais, porcentagem e dias) e catálogo dos indicadores educacionais.
import { t } from '../../i18n/index.js';
import { formatCompactNumber, formatCurrency, formatDate, formatDateTime, formatNumber } from '../../lib/format.js';

/**
 * @typedef {import('../../data/dashboards.js').NumberFormat} NumberFormat
 * @typedef {import('../../data/dashboards.js').TableColumnFormat} TableColumnFormat
 */

/**
 * @param {number | string | null | undefined} value
 * @param {NumberFormat | TableColumnFormat | string | null | undefined} format
 * @param {{ compact?: boolean }} [options]
 */
export function formatMetricValue(value, format, { compact = false } = {}) {
  if (value === null || value === undefined || value === '') return '-';
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return String(value);
  switch (format) {
    case 'CURRENCY':
      return formatCurrency(numericValue, 'BRL', { short: compact && Math.abs(numericValue) >= 10_000 });
    case 'PERCENT':
      // O contrato manda porcentagem na escala 0 a 100.
      return formatNumber(numericValue, { percentage: true, decimals: Math.abs(numericValue) < 10 && !Number.isInteger(numericValue) ? 1 : 0 });
    case 'DAYS': {
      const rounded = Math.round(numericValue * 10) / 10;
      // "0 dia" soaria errado: só 1 fica no singular
      return t(rounded === 1 ? 'dashboards.format.day' : 'dashboards.format.days', { value: formatNumber(rounded, { decimals: Number.isInteger(rounded) ? 0 : 1 }) });
    }
    default:
      if (compact && Math.abs(numericValue) >= 10_000) return formatCompactNumber(numericValue);
      return formatNumber(numericValue, { decimals: Number.isInteger(numericValue) ? 0 : undefined });
  }
}

/**
 * Valor de célula de tabela conforme o formato da coluna.
 * @param {any} value
 * @param {TableColumnFormat | undefined} format
 */
export function formatCellValue(value, format) {
  if (value === null || value === undefined || value === '') return '';
  if (format === 'DATE') return formatDate(String(value));
  if (format === 'DATE_TIME') return formatDateTime(String(value));
  if (format === 'TEXT' || format === 'STATUS') return String(value);
  if (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)))) {
    return formatMetricValue(value, format ?? 'NUMBER');
  }
  return String(value);
}

/**
 * Indicadores educacionais (KPI) do contrato, seção 2. `lowerIsBetter` inverte a cor da variação.
 * `visualization` diz como desenhar as métricas de série.
 * @type {{ metric: string, icon: string, kind: 'NUMBER' | 'SERIES' | 'TABLE', lowerIsBetter?: boolean, visualization?: 'BAR' | 'BAR_HORIZONTAL' | 'LINE' }[]}
 */
export const KPI_METRICS = [
  { metric: 'NEW_LEADS', icon: 'IconUserPlus', kind: 'NUMBER' },
  { metric: 'VISITS', icon: 'IconCalendarEvent', kind: 'NUMBER' },
  { metric: 'ENROLLMENTS', icon: 'IconSchool', kind: 'NUMBER' },
  { metric: 'CONVERSION_RATE', icon: 'IconPercentage', kind: 'NUMBER' },
  { metric: 'ENROLLED_REVENUE', icon: 'IconCoins', kind: 'NUMBER' },
  { metric: 'PIPELINE_REVENUE', icon: 'IconMoneybag', kind: 'NUMBER' },
  { metric: 'AVERAGE_TICKET', icon: 'IconReceipt', kind: 'NUMBER' },
  { metric: 'TIME_TO_ENROLL', icon: 'IconHourglass', kind: 'NUMBER', lowerIsBetter: true },
  { metric: 'LOST', icon: 'IconUserX', kind: 'NUMBER', lowerIsBetter: true },
  { metric: 'AWAITING_REPLY', icon: 'IconMessages', kind: 'NUMBER', lowerIsBetter: true },
  { metric: 'FUNNEL_BY_STAGE', icon: 'IconChartBar', kind: 'SERIES', visualization: 'BAR' },
  { metric: 'ENROLLMENTS_OVER_TIME', icon: 'IconChartLine', kind: 'SERIES', visualization: 'LINE' },
  { metric: 'LEADS_AND_VISITS_BY_MONTH', icon: 'IconChartBar', kind: 'SERIES', visualization: 'BAR' },
  { metric: 'LOST_REASONS', icon: 'IconChartBar', kind: 'SERIES', visualization: 'BAR_HORIZONTAL' },
  { metric: 'SOURCE_CONVERSION', icon: 'IconTable', kind: 'TABLE' },
  { metric: 'CONSULTANT_RANKING', icon: 'IconTable', kind: 'TABLE' },
  { metric: 'CLASS_OCCUPANCY', icon: 'IconTable', kind: 'TABLE' },
  { metric: 'REENROLLMENT_STATUS', icon: 'IconTable', kind: 'TABLE' },
];

/**
 * @param {string | undefined} metric
 */
export function getKpiMetric(metric) {
  return KPI_METRICS.find((candidate) => candidate.metric === metric);
}

/**
 * Variação percentual contra o ano anterior; null quando não dá para comparar.
 * @param {number | null | undefined} value
 * @param {number | null | undefined} previous
 */
export function computeVariation(value, previous) {
  if (value === null || value === undefined || previous === null || previous === undefined) return null;
  if (Number(previous) === 0) return Number(value) === 0 ? 0 : null;
  return ((Number(value) - Number(previous)) / Math.abs(Number(previous))) * 100;
}
